"use client";

import type { useChat } from "@ai-sdk/react";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import dayjs from "dayjs";
import { graphql, type TadaDocumentNode } from "gql.tada";
import { UploadIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { IwImportUrlGroupButton } from "@/app/[lang]/[workspaceId]/_components/groups/import/iw-import-url-group-button";
import URLGroupInput, {
  type URLGroupInputValue,
} from "@/app/[lang]/[workspaceId]/_components/groups/url-group-input";
import { getWeekdayName } from "@/app/[lang]/[workspaceId]/iw/_util/weekday";
import { AutocompleteSitemapInput } from "@/app/[lang]/[workspaceId]/iw/components/autocomplete-sitemap-input";
import type { RegisteredProperty } from "@/app/[lang]/[workspaceId]/iw/components/iw-form-dialog";
import { PropertyCombobox } from "@/app/[lang]/[workspaceId]/iw/components/property-combobox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useSafeI18nDate } from "@/i18n/use-safe-i18n-date";
import type { AgentUIMessage } from "@/lib/ai/types";
import { useRouter } from "@/lib/navigation";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import { EDIT_INDEX_WORKER_NAME } from "./constants";

type Input = {
  organizationId: string;
  projectId?: string;
  projectName?: string;
  indexWorkerId?: string;
  indexWorkerName?: string;
  name?: string;
  note?: string;
  propertyId?: string;
  urlType?: "SITEMAP" | "RSS" | "MANUAL";
  samplingCount?: number;
  schedule?: { type: string; dayOfWeek?: number | number[] };
  sitemap?: string;
  rssFeed?: string;
  rssFeedSamplingPeriod?: number;
  urls?: string | string[];
  urlGroup?: {
    op: "AND" | "OR";
    patterns: { type: string; pattern: string }[];
  } | null;
  ignoreURLParameters?: string | string[];
};

type ScheduleType = "not-set" | "run-once" | "daily" | "weekly" | "custom";

type EditableForm = {
  projectName: string;
  name: string;
  note: string;
  propertyId: string;
  urlType: "SITEMAP" | "RSS" | "MANUAL";
  sitemap: string;
  rssFeed: string;
  rssFeedSamplingPeriod: string;
  urlsText: string;
  samplingCount: string;
  scheduleType: ScheduleType;
  customDayOfWeek: number[];
  useUrlGroup: boolean;
  urlGroup: URLGroupInputValue;
  useIgnoreURLParameters: boolean;
  ignoreURLParametersText: string;
};

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

type SitemapItem = {
  url: string;
  type: "index" | "regular";
  parentUrl?: string;
};

type ContextUrlGroupPattern = {
  type: URLGroupInputValue["patterns"][number]["type"];
  pattern: string;
};

type ContextWorker = {
  id: string;
  name: string;
  note?: string | null;
  samplingCount: number;
  schedule?: string | null;
  urlType: "SITEMAP" | "RSS" | "MANUAL";
  propertyID: string;
  sitemap?: string | null;
  rssFeed?: string | null;
  rssFeedSamplingPeriod?: number | null;
  urls?: readonly string[] | null;
  ignoreURLParameters?: readonly string[] | null;
  urlGroup?: {
    op: URLGroupInputValue["op"];
    patterns: readonly (ContextUrlGroupPattern | null)[];
  } | null;
};

type ContextProject = {
  __typename: "IndexWorkerProject";
  id: string;
  name: string;
  indexWorkers?: readonly (ContextWorker | null)[] | null;
};

type ContextOrganization = {
  __typename: "Organization";
  id: string;
  registeredProperties?: readonly (RegisteredProperty | null)[] | null;
  projects?:
    | readonly ({ __typename?: string } | ContextProject | null)[]
    | null;
};

type EditIndexWorkerContextResult = {
  node?: ContextOrganization | { __typename?: string } | null;
};

type EditIndexWorkerContextVariables = {
  organizationId: string;
};

type PropertySitemapsVariables = {
  id: string;
};

const DAY_OPTIONS = [0, 1, 2, 3, 4, 5, 6] as const;
const RSS_FEED_SAMPLING_PERIOD_VALUES = [0, 1, 3, 7, 28, 365] as const;
const RSS_FEED_SAMPLING_PERIOD_SET = new Set<number>(
  RSS_FEED_SAMPLING_PERIOD_VALUES,
);

// trace 上で支配的だった tool UI query は、document 文字列を wide string にして
// gql.tada の型レベル parse を局所的に避けるため、query 本体と手書き型の同期に注意する。
const getEditIndexWorkerContextQueryDocument: string = `
  query GetEditIndexWorkerContextForTool($organizationId: ID!) {
    node(id: $organizationId) {
      __typename
      ... on Organization {
        id
        registeredProperties {
          __typename
          ... on RegisteredSearchConsoleUserProperty {
            id
            property
            user {
              email
            }
          }
          ... on RegisteredSearchConsoleServiceAccountProperty {
            id
            property
            serviceAccount {
              email
            }
          }
        }
        projects {
          __typename
          ... on IndexWorkerProject {
            id
            name
            indexWorkers {
              id
              name
              note
              samplingCount
              schedule
              urlType
              propertyID
              sitemap
              rssFeed
              rssFeedSamplingPeriod
              urls
              ignoreURLParameters
              urlGroup {
                op
                patterns {
                  type
                  pattern
                }
              }
            }
          }
        }
      }
    }
  }
`;

const GetEditIndexWorkerContextQuery = graphql(
  getEditIndexWorkerContextQueryDocument,
) as TadaDocumentNode<
  EditIndexWorkerContextResult,
  EditIndexWorkerContextVariables
>;

type ResolvedTarget = {
  project: ContextProject;
  worker: ContextWorker;
};

// 上の context query と同じく、結果型は手書きで固定して型検査の再展開を抑える。
const getPropertySitemapsQueryDocument: string = `
  query GetPropertySitemapsForEditIndexWorkerTool($id: ID!) {
    node(id: $id) {
      __typename
      ... on RegisteredSearchConsoleUserProperty {
        id
        sitemaps {
          __typename
          ... on Sitemap {
            url
          }
          ... on SitemapIndex {
            url
            sitemaps {
              url
            }
          }
        }
      }
      ... on RegisteredSearchConsoleServiceAccountProperty {
        id
        sitemaps {
          __typename
          ... on Sitemap {
            url
          }
          ... on SitemapIndex {
            url
            sitemaps {
              url
            }
          }
        }
      }
    }
  }
`;

const GetPropertySitemapsQuery = graphql(
  getPropertySitemapsQueryDocument,
) as TadaDocumentNode<unknown, PropertySitemapsVariables>;

function toStringArray(value: unknown): string[] {
  if (typeof value === "string") {
    return value
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (Array.isArray(value)) {
    return value.map((s) => String(s).trim()).filter(Boolean);
  }
  return [];
}

function scheduleTypeFromInput(
  schedule: Input["schedule"] | undefined,
): ScheduleType {
  const type = schedule?.type;
  if (
    type === "not-set" ||
    type === "run-once" ||
    type === "daily" ||
    type === "weekly" ||
    type === "custom"
  ) {
    return type;
  }
  return "not-set";
}

function normalizeCustomDays(dayOfWeek: unknown): number[] {
  if (!Array.isArray(dayOfWeek)) return [];
  return Array.from(
    new Set(
      dayOfWeek
        .map((d) => Number(d))
        .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6),
    ),
  ).sort((a, b) => a - b);
}

function defaultUrlGroup(
  value: Input["urlGroup"] | undefined,
): URLGroupInputValue {
  if (value && Array.isArray(value.patterns) && value.patterns.length > 0) {
    return {
      op: value.op,
      patterns: value.patterns.map((pattern) => ({
        type: (pattern.type ||
          "CONTAINS") as URLGroupInputValue["patterns"][number]["type"],
        pattern: pattern.pattern ?? "",
      })),
    };
  }

  return {
    op: "AND",
    patterns: [{ type: "CONTAINS", pattern: "" }],
  };
}

function normalizeName(value: string): string {
  return value.trim().toLowerCase();
}

function parseRetainedSchedule(
  schedule: string | null | undefined,
): Input["schedule"] {
  if (!schedule) {
    return { type: "not-set" };
  }

  const parts = schedule.split(" ");
  if (parts.length !== 3) {
    return { type: "not-set" };
  }

  const dayOfWeekPart = parts[2];
  if (!dayOfWeekPart) {
    return { type: "not-set" };
  }
  if (dayOfWeekPart === "*") {
    return { type: "daily" };
  }

  const customDayOfWeek = Array.from(
    new Set(
      dayOfWeekPart
        .split(",")
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value))
        .map((value) => ((value % 7) + 7) % 7)
        .filter((value) => value >= 0 && value <= 6),
    ),
  ).sort((a, b) => a - b);

  if (customDayOfWeek.length === 0) {
    return { type: "not-set" };
  }

  if (customDayOfWeek.length === 1) {
    return {
      type: "weekly",
      dayOfWeek: customDayOfWeek[0],
    };
  }

  return {
    type: "custom",
    dayOfWeek: customDayOfWeek,
  };
}

function toRetainedInput(args: {
  target: ResolvedTarget;
  organizationId: string;
}): Partial<Input> {
  const { target, organizationId } = args;
  const { project, worker } = target;

  const urlGroup =
    worker.urlGroup && worker.urlGroup.patterns.length > 0
      ? {
          op: worker.urlGroup.op,
          patterns: worker.urlGroup.patterns
            .filter((pattern): pattern is ContextUrlGroupPattern =>
              Boolean(pattern),
            )
            .map((pattern) => ({
              type: pattern.type,
              pattern: pattern.pattern,
            })),
        }
      : undefined;

  return {
    organizationId,
    projectId: project.id,
    projectName: project.name,
    indexWorkerId: worker.id,
    indexWorkerName: worker.name,
    name: worker.name,
    note: worker.note ?? "",
    propertyId: worker.propertyID,
    urlType: worker.urlType,
    samplingCount: worker.samplingCount,
    schedule: parseRetainedSchedule(worker.schedule),
    sitemap: worker.sitemap ?? "",
    rssFeed: worker.rssFeed ?? "",
    rssFeedSamplingPeriod: worker.rssFeedSamplingPeriod ?? 7,
    urls: worker.urls ? [...worker.urls] : [],
    urlGroup,
    ignoreURLParameters: worker.ignoreURLParameters
      ? [...worker.ignoreURLParameters]
      : [],
  };
}

function mergeRetainedAndDraftInput(args: {
  retained: Partial<Input>;
  draft: Partial<Input>;
}): Partial<Input> {
  const { retained, draft } = args;

  return {
    organizationId: draft.organizationId ?? retained.organizationId ?? "",
    projectId: draft.projectId ?? retained.projectId,
    projectName: draft.projectName ?? retained.projectName,
    indexWorkerId: draft.indexWorkerId ?? retained.indexWorkerId,
    indexWorkerName: draft.indexWorkerName ?? retained.indexWorkerName,
    name: draft.name ?? retained.name,
    note: typeof draft.note === "string" ? draft.note : retained.note,
    propertyId: draft.propertyId ?? retained.propertyId,
    urlType: draft.urlType ?? retained.urlType,
    samplingCount: draft.samplingCount ?? retained.samplingCount,
    schedule: draft.schedule ?? retained.schedule,
    sitemap: draft.sitemap ?? retained.sitemap,
    rssFeed: draft.rssFeed ?? retained.rssFeed,
    rssFeedSamplingPeriod:
      draft.rssFeedSamplingPeriod ?? retained.rssFeedSamplingPeriod,
    urls: draft.urls ?? retained.urls,
    // `urlGroup: null` means an explicit clear request and must not fallback.
    urlGroup: Object.prototype.hasOwnProperty.call(draft, "urlGroup")
      ? draft.urlGroup
      : retained.urlGroup,
    ignoreURLParameters:
      draft.ignoreURLParameters ?? retained.ignoreURLParameters,
  };
}

function resolveTarget(args: {
  input: Partial<Input>;
  projects: ContextProject[];
}): { target?: ResolvedTarget; error?: string } {
  const { input, projects } = args;
  const allTargets = projects.flatMap((project) =>
    (project.indexWorkers ?? [])
      .filter((worker): worker is ContextWorker => Boolean(worker))
      .map((worker) => ({ project, worker })),
  );

  if (allTargets.length === 0) {
    return { error: "No Index Workers found in this organization." };
  }

  if (
    typeof input.indexWorkerId === "string" &&
    input.indexWorkerId.length > 0
  ) {
    const found = allTargets.find(
      ({ worker }) => worker.id === input.indexWorkerId,
    );
    if (!found) {
      return { error: "Specified Index Worker ID was not found." };
    }
    return { target: found };
  }

  let targetProject: ContextProject | undefined;
  if (typeof input.projectId === "string" && input.projectId.length > 0) {
    targetProject = projects.find((project) => project.id === input.projectId);
    if (!targetProject) {
      return { error: "Specified project ID was not found." };
    }
  } else if (
    typeof input.projectName === "string" &&
    input.projectName.trim().length > 0
  ) {
    const exact = projects.filter(
      (project) => project.name === input.projectName,
    );
    const normalized = projects.filter(
      (project) =>
        normalizeName(project.name) === normalizeName(input.projectName!),
    );
    const candidates = exact.length > 0 ? exact : normalized;
    if (candidates.length !== 1) {
      return {
        error: "Target project is ambiguous. Please specify project name/id.",
      };
    }
    targetProject = candidates[0]!;
  }

  if (targetProject) {
    const workers = (targetProject.indexWorkers ?? []).filter(
      (worker): worker is ContextWorker => Boolean(worker),
    );
    if (workers.length === 0) {
      return { error: "No Index Workers found in the selected project." };
    }

    if (
      typeof input.indexWorkerName === "string" &&
      input.indexWorkerName.trim().length > 0
    ) {
      const exact = workers.filter(
        (worker) => worker.name === input.indexWorkerName,
      );
      const normalized = workers.filter(
        (worker) =>
          normalizeName(worker.name) === normalizeName(input.indexWorkerName!),
      );
      const candidates = exact.length > 0 ? exact : normalized;
      if (candidates.length !== 1) {
        return {
          error:
            "Target Index Worker is ambiguous in the selected project. Please specify a unique name.",
        };
      }
      return {
        target: {
          project: targetProject,
          worker: candidates[0]!,
        },
      };
    }

    if (workers.length !== 1) {
      return {
        error:
          "Target Index Worker is ambiguous in the selected project. Please specify indexWorkerName.",
      };
    }

    return {
      target: {
        project: targetProject,
        worker: workers[0]!,
      },
    };
  }

  if (
    typeof input.indexWorkerName === "string" &&
    input.indexWorkerName.trim().length > 0
  ) {
    const exact = allTargets.filter(
      ({ worker }) => worker.name === input.indexWorkerName,
    );
    const normalized = allTargets.filter(
      ({ worker }) =>
        normalizeName(worker.name) === normalizeName(input.indexWorkerName!),
    );
    const candidates = exact.length > 0 ? exact : normalized;
    if (candidates.length !== 1) {
      return {
        error:
          "Target Index Worker is ambiguous across projects. Please specify projectName/projectId.",
      };
    }
    return { target: candidates[0]! };
  }

  if (allTargets.length !== 1) {
    return {
      error:
        "Target Index Worker is not specified. Please provide indexWorkerId/indexWorkerName.",
    };
  }

  return { target: allTargets[0]! };
}

function createInitialForm(input: Partial<Input>): EditableForm {
  const urlsText = toStringArray(input.urls).join("\n");
  const ignoreURLParametersText = toStringArray(input.ignoreURLParameters).join(
    "\n",
  );
  const scheduleType = scheduleTypeFromInput(input.schedule);

  return {
    projectName: input.projectName ?? "",
    name: input.name ?? "",
    note: input.note ?? "",
    propertyId: input.propertyId ?? "",
    urlType: input.urlType ?? "SITEMAP",
    sitemap: input.sitemap ?? "",
    rssFeed: input.rssFeed ?? "",
    rssFeedSamplingPeriod: String(
      RSS_FEED_SAMPLING_PERIOD_SET.has(Number(input.rssFeedSamplingPeriod))
        ? Number(input.rssFeedSamplingPeriod)
        : 7,
    ),
    urlsText,
    samplingCount: String(input.samplingCount ?? 500),
    scheduleType,
    customDayOfWeek:
      scheduleType === "custom"
        ? normalizeCustomDays(input.schedule?.dayOfWeek)
        : [],
    useUrlGroup: Boolean(input.urlGroup),
    urlGroup: defaultUrlGroup(input.urlGroup),
    useIgnoreURLParameters: ignoreURLParametersText.length > 0,
    ignoreURLParametersText,
  };
}

function toggleCustomDay(
  days: number[],
  day: number,
  checked: boolean,
): number[] {
  if (checked) {
    return Array.from(new Set([...days, day])).sort((a, b) => a - b);
  }
  return days.filter((d) => d !== day);
}

function toSchedule(
  form: EditableForm,
  weeklyDayOfWeek?: number,
): Input["schedule"] {
  if (form.scheduleType === "weekly") {
    return {
      type: "weekly",
      dayOfWeek:
        typeof weeklyDayOfWeek === "number"
          ? weeklyDayOfWeek
          : new Date().getDay(),
    };
  }

  if (form.scheduleType === "custom") {
    return {
      type: "custom",
      dayOfWeek: Array.from(new Set(form.customDayOfWeek)).sort(
        (a, b) => a - b,
      ),
    };
  }

  return { type: form.scheduleType };
}

function compact<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, v]) => typeof v !== "undefined"),
  ) as T;
}

function flattenSitemapCandidates(data: unknown): SitemapItem[] {
  const node =
    data && typeof data === "object" && "node" in data
      ? (data as { node?: unknown }).node
      : null;
  if (!node || typeof node !== "object") return [];

  const type =
    "__typename" in node
      ? (node as { __typename?: string }).__typename
      : undefined;
  if (
    type !== "RegisteredSearchConsoleUserProperty" &&
    type !== "RegisteredSearchConsoleServiceAccountProperty"
  ) {
    return [];
  }

  const sitemaps =
    "sitemaps" in node ? (node as { sitemaps?: unknown[] }).sitemaps : [];
  if (!Array.isArray(sitemaps)) return [];

  const rows: SitemapItem[] = [];
  for (const sitemap of sitemaps) {
    if (!sitemap || typeof sitemap !== "object") continue;

    const url =
      "url" in sitemap ? (sitemap as { url?: string }).url : undefined;
    if (typeof url === "string" && url.length > 0) {
      rows.push({
        url,
        type: "regular",
      });
    }

    const typename =
      "__typename" in sitemap
        ? (sitemap as { __typename?: string }).__typename
        : undefined;
    if (typename !== "SitemapIndex") continue;

    const children =
      "sitemaps" in sitemap
        ? (sitemap as { sitemaps?: { url?: string }[] }).sitemaps
        : [];
    if (!Array.isArray(children)) continue;

    for (const child of children) {
      if (typeof child?.url === "string" && child.url.length > 0) {
        rows.push({
          url: child.url,
          type: "regular",
          parentUrl: url,
        });
      }
    }
  }

  const seen = new Set<string>();
  return rows.filter((row) => {
    if (seen.has(row.url)) return false;
    seen.add(row.url);
    return true;
  });
}

export function EditIndexWorkerConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const { t, i18n } = useLingui();
  const { safeI18nDate } = useSafeI18nDate();
  const router = useRouter();
  const toolName = isToolUIPart(part) ? getToolName(part) : null;
  const isTarget = toolName === EDIT_INDEX_WORKER_NAME;
  const isReady = isTarget && part.state === "input-available";
  const input = (part.input ?? {}) as Partial<Input>;

  const [form, setForm] = useState<EditableForm>(() =>
    createInitialForm(input),
  );
  const [prefilledToolCallId, setPrefilledToolCallId] = useState<string | null>(
    null,
  );
  const csvInput = useRef<HTMLInputElement>(null);

  const organizationId =
    isReady &&
    typeof input.organizationId === "string" &&
    input.organizationId.length > 0
      ? input.organizationId
      : null;
  const resolvedOrganizationId = organizationId ?? "";

  const [
    { data: contextData, error: contextError, fetching: contextFetching },
  ] = useQuery({
    query: GetEditIndexWorkerContextQuery,
    variables: { organizationId: resolvedOrganizationId },
    pause: !organizationId,
  });

  const organization =
    contextData?.node?.__typename === "Organization"
      ? (contextData.node as ContextOrganization)
      : null;
  const projects = (organization?.projects ?? []).filter(
    (project): project is ContextProject =>
      project?.__typename === "IndexWorkerProject",
  );
  const targetResolution = resolveTarget({ input, projects });
  const resolvedTarget = targetResolution.target;

  const mergedInput = resolvedTarget
    ? mergeRetainedAndDraftInput({
        retained: toRetainedInput({
          target: resolvedTarget,
          organizationId: resolvedOrganizationId,
        }),
        draft: input,
      })
    : undefined;

  useEffect(() => {
    if (!isReady || !mergedInput) return;
    if (prefilledToolCallId === part.toolCallId) return;

    setForm(createInitialForm(mergedInput));
    setPrefilledToolCallId(part.toolCallId);
  }, [isReady, mergedInput, part.toolCallId, prefilledToolCallId]);

  const [
    {
      data: propertySitemapsData,
      error: propertySitemapsError,
      fetching: propertySitemapsFetching,
    },
  ] = useQuery({
    query: GetPropertySitemapsQuery,
    variables: { id: form.propertyId },
    pause: !form.propertyId || form.urlType !== "SITEMAP",
  });

  if (!isReady) return null;

  if (!organizationId) {
    return (
      <div className="rounded-xl border bg-background p-5 text-sm text-foreground shadow-sm space-y-4">
        <Alert variant="destructive" title="Error">
          <Trans>organizationId is missing.</Trans>
        </Alert>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    );
  }

  if (contextFetching) {
    return (
      <div className="rounded-xl border bg-background p-5 text-sm text-foreground shadow-sm space-y-4">
        <Alert>
          <AlertDescription>
            <Trans>
              Loading retained values for the target Index Worker...
            </Trans>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (contextError || !organization || !resolvedTarget || !mergedInput) {
    return (
      <div className="rounded-xl border bg-background p-5 text-sm text-foreground shadow-sm space-y-4">
        <Alert variant="destructive" title="Error">
          <Trans>Failed to resolve target Index Worker for confirmation.</Trans>
          {targetResolution.error ? (
            <div className="mt-1">{targetResolution.error}</div>
          ) : null}
        </Alert>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    );
  }

  if (propertySitemapsError) {
    return (
      <div className="rounded-xl border bg-background p-5 text-sm text-foreground shadow-sm space-y-4">
        <Alert variant="destructive" title="Error">
          <Trans>
            Failed to load related information for confirmation. Please cancel
            and try again.
          </Trans>
        </Alert>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    );
  }

  const projectId = resolvedTarget.project.id;

  const propertyOptions = (organization.registeredProperties ?? []).filter(
    (property): property is RegisteredProperty => Boolean(property),
  );

  const draftWeeklyDayOfWeek =
    mergedInput.schedule?.type === "weekly" &&
    typeof mergedInput.schedule.dayOfWeek === "number"
      ? mergedInput.schedule.dayOfWeek
      : undefined;

  const sitemapCandidates = flattenSitemapCandidates(propertySitemapsData);

  const sampledUrlCount = toStringArray(form.urlsText).length;

  const samplingCount = Number(form.samplingCount);
  const rssSamplingPeriod = Number(form.rssFeedSamplingPeriod);
  const urlGroupPatterns = form.urlGroup.patterns
    .map((pattern) => ({
      type: pattern.type,
      pattern: pattern.pattern.trim(),
    }))
    .filter((pattern) => pattern.pattern.length > 0);

  const validationErrors: string[] = [];
  if (form.name.trim().length === 0) {
    validationErrors.push("Name is required.");
  }
  if (form.propertyId.trim().length === 0) {
    validationErrors.push("Search Console property is required.");
  }
  if (
    !Number.isInteger(samplingCount) ||
    samplingCount < 1 ||
    samplingCount > 2000
  ) {
    validationErrors.push("Sample size must be an integer between 1 and 2000.");
  }

  if (form.urlType === "SITEMAP" && form.sitemap.trim().length === 0) {
    validationErrors.push("Sitemap URL is required for SITEMAP source.");
  }
  if (form.urlType === "RSS") {
    if (form.rssFeed.trim().length === 0) {
      validationErrors.push("RSS feed URL is required for RSS source.");
    }
    if (!RSS_FEED_SAMPLING_PERIOD_SET.has(rssSamplingPeriod)) {
      validationErrors.push(
        "Sampling period must be one of: 1, 3, 7, 28, 365, or 0 (all time).",
      );
    }
  }
  if (form.urlType === "MANUAL" && sampledUrlCount === 0) {
    validationErrors.push(
      "At least one manual URL is required for MANUAL source.",
    );
  }

  if (form.scheduleType === "custom" && form.customDayOfWeek.length === 0) {
    validationErrors.push("At least one custom day of week is required.");
  }

  if (form.useUrlGroup && urlGroupPatterns.length === 0) {
    validationErrors.push("URL group requires at least one non-empty pattern.");
  }

  const overrideInput = compact({
    organizationId: organizationId ?? input.organizationId,
    projectId,
    indexWorkerId: resolvedTarget.worker.id,
    name: form.name.trim(),
    note: form.note,
    propertyId: form.propertyId,
    samplingCount,
    schedule: toSchedule(form, draftWeeklyDayOfWeek),
    sitemap: form.urlType === "SITEMAP" ? form.sitemap.trim() : undefined,
    rssFeed: form.urlType === "RSS" ? form.rssFeed.trim() : undefined,
    rssFeedSamplingPeriod:
      form.urlType === "RSS" ? rssSamplingPeriod : undefined,
    urls: form.urlType === "MANUAL" ? form.urlsText : undefined,
    urlGroup: form.useUrlGroup
      ? {
          op: form.urlGroup.op,
          patterns: urlGroupPatterns,
        }
      : null,
    ignoreURLParameters: form.useIgnoreURLParameters
      ? form.ignoreURLParametersText
      : "",
  });

  const handleClickUploadCSV = () => {
    csvInput.current?.click();
  };

  const handleChangeUploadCSV = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    setForm((prev) => ({
      ...prev,
      urlsText: text,
    }));
  };

  return (
    <div className="rounded-xl border bg-background text-sm text-foreground shadow-sm overflow-hidden">
      <div className="border-b bg-muted/30 px-5 py-4 space-y-1">
        <div className="font-medium">
          <Trans>Edit Index Worker</Trans>
        </div>
        <div className="text-sm text-muted-foreground">
          <Trans>
            Fill in the following information to update this Index Worker job.
          </Trans>
        </div>
      </div>

      <div className="@container p-5 space-y-5">
        <div className="grid grid-cols-1 gap-4 @xl:grid-cols-2 @xl:divide-x-1">
          <div className="space-y-4 @xl:pr-8">
            {!projectId && (
              <div>
                <Label>
                  <Trans>Project</Trans>
                </Label>
                <Input
                  value={form.projectName}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      projectName: event.target.value,
                    }))
                  }
                  placeholder={t`Select a project`}
                  required
                />
              </div>
            )}

            <div>
              <Label>
                <Trans>Job name</Trans>
              </Label>
              <Input
                value={form.name}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, name: event.target.value }))
                }
                required
              />
            </div>

            <div>
              <Label>
                <Trans>Note</Trans>
                <span className="text-muted-foreground">
                  {" "}
                  - <Trans>Optional</Trans>
                </span>
              </Label>
              <Textarea
                value={form.note}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, note: event.target.value }))
                }
                placeholder=""
              />
            </div>

            <div>
              <Label>
                <Trans>Search Console property</Trans>
              </Label>
              <PropertyCombobox
                id="create-iw-confirm-property"
                properties={propertyOptions}
                value={form.propertyId}
                onValueChange={(propertyId) =>
                  setForm((prev) => ({ ...prev, propertyId }))
                }
                placeholder={t`Select a property`}
                searchPlaceholder={t`Search property...`}
                required
              />
            </div>

            <div>
              <Label>
                <Trans>URL type</Trans>
              </Label>
              <Select
                value={form.urlType}
                onValueChange={(urlType) =>
                  setForm((prev) => ({
                    ...prev,
                    urlType: urlType as EditableForm["urlType"],
                  }))
                }
                disabled
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder={t`Select a URL type`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SITEMAP">
                    <Trans>XML sitemap</Trans>
                  </SelectItem>
                  <SelectItem value="RSS">
                    <Trans>RSS feed</Trans>
                  </SelectItem>
                  <SelectItem value="MANUAL">
                    <Trans>Manual input or CSV upload</Trans>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                <Trans>
                  URL type cannot be changed once the Index Worker job is
                  created.
                </Trans>
              </p>
            </div>

            {form.urlType === "SITEMAP" && (
              <div>
                <Label>
                  <Trans>XML sitemap</Trans>
                </Label>
                {sitemapCandidates.length > 0 ? (
                  <AutocompleteSitemapInput
                    value={form.sitemap}
                    onChange={(value) =>
                      setForm((prev) => ({ ...prev, sitemap: value }))
                    }
                    sitemaps={sitemapCandidates}
                    placeholder={t`Enter sitemap URL or search...`}
                    required
                  />
                ) : (
                  <Input
                    type="url"
                    value={form.sitemap}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        sitemap: event.target.value,
                      }))
                    }
                    required
                  />
                )}
                <p className="text-sm text-muted-foreground">
                  <Trans>Both XML and text sitemaps are supported.</Trans>
                </p>
                {sitemapCandidates.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    <Plural
                      value={sitemapCandidates.length}
                      one="# sitemap"
                      other="# sitemaps"
                    />
                    <Trans> detected from the property.</Trans>
                    {propertySitemapsFetching && (
                      <>
                        {" "}
                        <Trans>(refreshing...)</Trans>
                      </>
                    )}
                  </p>
                )}
              </div>
            )}

            {form.urlType === "RSS" && (
              <>
                <div>
                  <Label>
                    <Trans>RSS feed</Trans>
                  </Label>
                  <Input
                    type="url"
                    value={form.rssFeed}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        rssFeed: event.target.value,
                      }))
                    }
                    required
                  />
                </div>

                <div>
                  <Label>
                    <Trans>Sampling period</Trans>
                  </Label>
                  <Select
                    value={form.rssFeedSamplingPeriod}
                    onValueChange={(value) =>
                      setForm((prev) => ({
                        ...prev,
                        rssFeedSamplingPeriod: value,
                      }))
                    }
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t`Select a sampling period`} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">
                        <Trans>1 day</Trans>
                      </SelectItem>
                      <SelectItem value="3">
                        <Trans>3 days</Trans>
                      </SelectItem>
                      <SelectItem value="7">
                        <Trans>A week</Trans>
                      </SelectItem>
                      <SelectItem value="28">
                        <Trans>4 weeks</Trans>
                      </SelectItem>
                      <SelectItem value="365">
                        <Trans>1 year</Trans>
                      </SelectItem>
                      <SelectItem value="0">
                        <Trans>All time</Trans>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {form.urlType === "MANUAL" && (
              <div>
                <Label>
                  <Trans>URLs</Trans>
                </Label>
                <Textarea
                  value={form.urlsText}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      urlsText: event.target.value,
                    }))
                  }
                  required
                />

                <p className="text-sm text-muted-foreground flex items-center">
                  <Trans>Enter one URL per line.</Trans>
                  <Button
                    variant="link"
                    className="p-0 ml-2"
                    type="button"
                    onClick={handleClickUploadCSV}
                  >
                    <UploadIcon className="w-4 h-4" />
                    <Trans>Upload CSV</Trans>
                  </Button>
                  <input
                    type="file"
                    className="hidden"
                    accept=".csv"
                    ref={csvInput}
                    onChange={handleChangeUploadCSV}
                  />
                </p>
              </div>
            )}
          </div>

          <div className="space-y-4 @xl:pl-4">
            <div>
              <Label>
                <Trans>Sample size (Max 2000)</Trans>
              </Label>
              <Input
                type="number"
                min={1}
                max={2000}
                value={form.samplingCount}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    samplingCount: event.target.value,
                  }))
                }
                required
              />
              <p className="text-sm text-muted-foreground">
                <Trans>
                  If the number of URLs exceeds the sample size, sampling will
                  be performed.
                </Trans>
              </p>
            </div>

            <div>
              <Label>
                <Trans>Schedule</Trans>
              </Label>
              <Select
                value={form.scheduleType}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    scheduleType: value as ScheduleType,
                  }))
                }
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder={t`Select a schedule`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not-set">
                    <Trans>Not set</Trans>
                  </SelectItem>
                  <SelectItem value="run-once">
                    <Trans>Run once</Trans>{" "}
                    <strong>
                      (<Trans>Executes immediately</Trans>)
                    </strong>
                  </SelectItem>
                  <SelectItem value="daily">
                    <Trans>Daily</Trans>
                  </SelectItem>
                  <SelectItem value="weekly">
                    <Trans>
                      Weekly on{" "}
                      {safeI18nDate(dayjs().toDate(), { weekday: "long" })}
                    </Trans>
                  </SelectItem>
                  <SelectItem value="custom">
                    <Trans>Custom</Trans>
                  </SelectItem>
                </SelectContent>
              </Select>

              {form.scheduleType === "custom" && (
                <div className="space-y-2 mt-2">
                  <div>
                    <Label>
                      <Trans>Day of week</Trans>
                    </Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {DAY_OPTIONS.map((index) => (
                        <Label
                          key={index}
                          className="inline-flex items-center gap-2"
                        >
                          <Checkbox
                            checked={form.customDayOfWeek.includes(index)}
                            onCheckedChange={(checked) =>
                              setForm((prev) => ({
                                ...prev,
                                customDayOfWeek: toggleCustomDay(
                                  prev.customDayOfWeek,
                                  index,
                                  Boolean(checked),
                                ),
                              }))
                            }
                          />
                          <span>{getWeekdayName(i18n, index, "short")}</span>
                        </Label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="border rounded-md p-4 bg-muted/10 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Checkbox
                  id="use-ignore-url-parameters-confirm"
                  checked={form.useIgnoreURLParameters}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({
                      ...prev,
                      useIgnoreURLParameters: Boolean(checked),
                    }))
                  }
                />
                <Label
                  htmlFor="use-ignore-url-parameters-confirm"
                  className="font-medium"
                >
                  <Trans>Ignoring URL parameters</Trans>
                </Label>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                <Trans>
                  Specify URL parameters to ignore. For example, if you specify
                  "utm_source", URLs like "example.com?utm_source=google" and
                  "example.com" will be treated as the same URL.
                </Trans>
              </p>
              {form.useIgnoreURLParameters && (
                <div className="mt-2">
                  <p className="text-sm text-muted-foreground mb-2">
                    <Trans>Enter one parameter per line.</Trans>
                  </p>
                  <Textarea
                    value={form.ignoreURLParametersText}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        ignoreURLParametersText: event.target.value,
                      }))
                    }
                    className="h-24"
                    placeholder={t`e.g. utm_source\nutm_medium\nutm_campaign`}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border rounded-md p-4 bg-muted/10 mb-1">
          <div className="flex items-center gap-2 mb-2">
            <Checkbox
              id="use-url-group-confirm"
              checked={form.useUrlGroup}
              onCheckedChange={(checked) =>
                setForm((prev) => ({
                  ...prev,
                  useUrlGroup: Boolean(checked),
                }))
              }
            />
            <Label htmlFor="use-url-group-confirm" className="font-medium">
              <Trans>Filter URLs with URL Group</Trans>
            </Label>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            <Trans>
              URL Group allows you to filter URLs from the source (XML sitemap,
              RSS feed, or manual URLs) before sampling. Only URLs that match
              the specified patterns will be processed.
            </Trans>
          </p>
          {form.useUrlGroup && (
            <div className="mt-4 bg-background p-4 rounded-md border flex flex-col gap-3 md:flex-row md:items-start">
              <div className="min-w-0 flex-1">
                <URLGroupInput
                  value={form.urlGroup}
                  onChange={(urlGroup) =>
                    setForm((prev) => ({ ...prev, urlGroup }))
                  }
                />
              </div>
              {organizationId && (
                <div className="flex-shrink-0 md:mt-6.5 md:ml-2">
                  <IwImportUrlGroupButton
                    workspaceId={organizationId}
                    onChange={(urlGroup) =>
                      setForm((prev) => ({ ...prev, urlGroup }))
                    }
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-3 border-t pt-3">
          {validationErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertDescription>
                <ul className="list-disc pl-5 space-y-1 [overflow-wrap:anywhere]">
                  {validationErrors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center justify-end gap-2">
            {validationErrors.length === 0 ? (
              <ApproveButton
                toolCallId={part.toolCallId}
                toolName={getToolName(part)}
                addToolResult={addToolResult}
                sendMessage={sendMessage}
                onSuccess={() => router.refresh()}
                overrideInput={overrideInput}
              >
                <Trans>Update</Trans>
              </ApproveButton>
            ) : (
              <Button size="sm" disabled>
                <Trans>Update</Trans>
              </Button>
            )}
            <DenyButton
              toolCallId={part.toolCallId}
              toolName={getToolName(part)}
              addToolResult={addToolResult}
              sendMessage={sendMessage}
            >
              <Trans>Cancel</Trans>
            </DenyButton>
          </div>
        </div>
      </div>
    </div>
  );
}
